import { DestroyRef, Directive, ElementRef, HostListener, inject, Input, input, OnDestroy, ViewContainerRef } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CeContextMenuComponent } from './ce-context-menu.component';
import { CeOverlayContainerService } from '../ce-overlay/ce-overlay-container.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, take, tap } from 'rxjs';

type ContextMenuPosition =
    | 'top-left'
    | 'top-middle'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-middle'
    | 'bottom-right'
    | 'above'
    | 'below'
    | 'left'
    | 'right'
    | undefined;

/**
 * Helper function to create a position configuration.
 */
const createPosition = (
    originX: 'start' | 'center' | 'end',
    originY: 'top' | 'center' | 'bottom',
    overlayX: 'start' | 'center' | 'end',
    overlayY: 'top' | 'center' | 'bottom',
    offsetX = 0,
    offsetY = 0
): ConnectedPosition => ({
    originX,
    originY,
    overlayX,
    overlayY,
    offsetX,
    offsetY,
});

/**
 * Position strategy configurations for context menu positioning.
 * Each position has a primary placement and a fallback.
 */
const POSITION_STRATEGIES: Record<string, ConnectedPosition[]> = {
    above: [
        createPosition('center', 'top', 'center', 'bottom', 0, -8), // Above
        createPosition('center', 'bottom', 'center', 'top', 0, 8), // Fallback: Below
    ],
    below: [
        createPosition('center', 'bottom', 'center', 'top', 0, 8), // Below
        createPosition('center', 'top', 'center', 'bottom', 0, -8), // Fallback: Above
    ],
    left: [
        createPosition('start', 'center', 'end', 'center', -8, 0), // Left
        createPosition('end', 'center', 'start', 'center', 8, 0), // Fallback: Right
    ],
    right: [
        createPosition('end', 'center', 'start', 'center', 8, 0), // Right
        createPosition('start', 'center', 'end', 'center', -8, 0), // Fallback: Left
    ],
    'top-left': [
        createPosition('start', 'top', 'start', 'bottom', 0, -8),
        createPosition('start', 'bottom', 'start', 'top', 0, 8),
    ],
    'top-middle': [
        createPosition('center', 'top', 'center', 'bottom', 0, -8),
        createPosition('center', 'bottom', 'center', 'top', 0, 8),
    ],
    'top-right': [
        createPosition('end', 'top', 'end', 'bottom', 0, -8),
        createPosition('end', 'bottom', 'end', 'top', 0, 8),
    ],
    'bottom-left': [
        createPosition('start', 'bottom', 'start', 'top', 0, 8),
        createPosition('start', 'top', 'start', 'bottom', 0, -8),
    ],
    'bottom-middle': [
        createPosition('center', 'bottom', 'center', 'top', 0, 8),
        createPosition('center', 'top', 'center', 'bottom', 0, -8),
    ],
    'bottom-right': [
        createPosition('end', 'bottom', 'end', 'top', 0, 8),
        createPosition('end', 'top', 'end', 'bottom', 0, -8),
    ],
    default: [
        createPosition('start', 'bottom', 'start', 'top', 0, 8), // Position below target
        createPosition('start', 'top', 'start', 'bottom', 0, -8), // Position above target
        createPosition('end', 'bottom', 'end', 'top', 0, 8), // Position right of target
        createPosition('end', 'top', 'start', 'bottom', 0, -8), // Position left of target
    ],
};

@Directive({
    selector: `[ce-context-menu-trigger-for], [ceContextMenuTriggerFor]`,
    host: {
        class: 'ce-context-menu-trigger',
    },
    exportAs: 'ceContextMenuTrigger',
})
/**
 * Directive for triggering and managing context menus with flexible positioning.
 *
 * @example
 * ```html
 * <button [ceContextMenuTriggerFor]="menu" ceContextMenuPosition="top-right">
 *   Actions
 * </button>
 * <ce-context-menu #menu>
 *   <ce-list-option>Action 1</ce-list-option>
 * </ce-context-menu>
 * ```
 */
export class CeContextMenuTriggerDirective implements OnDestroy {
    /** Counter for generating unique menu IDs */
    static nextUniqueId = 0;

    #menuOpen = false;
    #overlayRef!: OverlayRef;
    #contextMenuOverlayContainer!: HTMLDivElement | null;
    #viewContainerRef = inject(ViewContainerRef);
    #overlayContainerService = inject(CeOverlayContainerService);
    #overlay = inject(Overlay);
    #element = inject(ElementRef<HTMLElement>);
    #uniqueId!: string;
    #router = inject(Router);
    #destroyRef = inject(DestroyRef);
    readonly #boundMenuMouseLeave = this.handleMenuMouseLeave.bind(this);

    #position: ContextMenuPosition = undefined;
    #triggerOnHover = false;

    /**
     * The container scope for the context menu overlay.
     * When set to 'dialog', the menu will be positioned within a dialog overlay container.
     * When empty, the menu will be appended to the document body.
     *
     * @default ''
     * @example
     * ```html
     * <button [ceContextMenuTriggerFor]="menu" ceContextMenuContainer="dialog">
     *   Actions
     * </button>
     * ```
     */
    ceContextMenuContainer = input<string>('');

    @Input('ceContextMenuTriggerFor') menu!: CeContextMenuComponent;

    @Input('ceContextMenuPosition')
    get contextMenuPosition(): ContextMenuPosition {
        return this.#position;
    }

    set contextMenuPosition(value: ContextMenuPosition) {
        this.#position = value;
    }

    @Input('ceContextMenuTriggerOnHover')
    get triggerOnHover(): boolean {
        return this.#triggerOnHover;
    }

    set triggerOnHover(value: boolean) {
        this.#triggerOnHover = value;
    }

    @HostListener('click', ['$event'])
    /**
     * Handles click events on the trigger element.
     * Toggles the menu open/closed state.
     *
     * @param event - The mouse click event
     */
    handleClick(event: MouseEvent) {
        event.stopPropagation();
        if (this.#menuOpen) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    @HostListener('document:mouseup', ['$event'])
    /**
     * Handles clicks outside the menu to close it.
     * Ignores clicks on the trigger button and inside the menu itself.
     *
     * @param event - The mouse click event
     */
    onClickOutside(event: MouseEvent) {
        if (!this.#menuOpen) return;

        let targetElement = event.target as HTMLElement;

        // Check if click is on the trigger button itself
        if (this.#element.nativeElement.contains(targetElement)) return;

        // Traverse up the DOM tree
        while (targetElement !== null) {
            // Check if the current element is this specific menu instance
            if (targetElement.id === this.#uniqueId) return;
            targetElement = targetElement.parentElement as HTMLElement;
        }

        // Click event is outside a ce-context-menu element
        // Click inside the menu must be handled before closing the menu
        // so we need for event loop to finish before closing
        setTimeout(() => this.closeMenu(), 0);
    }

    @HostListener('mouseenter', ['$event'])
    /**
     * Handles mouse enter events when hover trigger is enabled.
     * Opens the menu if triggerOnHover is true.
     *
     * @param $event - The mouse enter event
     */
    handleMouseEnter($event: MouseEvent) {
        $event.stopPropagation();
        if (this.#triggerOnHover) {
            this.openMenu();
        }
    }

    @HostListener('mouseleave', ['$event'])
    /**
     * Handles mouse leave events when hover trigger is enabled.
     * Closes the menu if the mouse moves outside both trigger and menu.
     *
     * @param $event - The mouse leave event
     */
    handleMouseLeave($event: MouseEvent) {
        $event.stopPropagation();
        const targetElement = $event.relatedTarget as HTMLElement;
        if (this.#isInsideSubMenu(targetElement)) {
            return;
        }

        // Click event is outside the context menu
        if (this.#triggerOnHover) {
            this.closeMenu();
        }
    }

    /**
     * Handles mouse leave events on the menu element itself.
     * Closes the menu when hover trigger is enabled and mouse leaves the menu.
     */
    handleMenuMouseLeave() {
        if (this.#triggerOnHover) {
            this.closeMenu();
        }
    }

    /**
     * Opens the context menu and positions it relative to the trigger element.
     * Handles overlay creation, event listeners, and routing cleanup.
     */
    openMenu(): void {
        if (!this.menu) return;

        if (this.#overlayRef?.hasAttached()) {
            this.closeMenu();
        }

        this.#createOverlayRef();

        this.#overlayRef.backdropClick().subscribe(() => this.closeMenu());

        // Generate unique ID before attaching to prevent race conditions
        this.#uniqueId = `ce-context-menu-${CeContextMenuTriggerDirective.nextUniqueId++}`;

        const menuPortal = new TemplatePortal(this.menu.templateRef, this.#viewContainerRef);

        this.#overlayRef.attach(menuPortal);
        this.#overlayRef.overlayElement.id = this.#uniqueId;
        this.#menuOpen = true;

        // Add listeners to the menu element
        const menuElement = this.#overlayRef.overlayElement;
        menuElement.addEventListener('mouseleave', this.#boundMenuMouseLeave);

        // Close menu on route changes
        this.#router.events
            .pipe(
                filter((event) => event instanceof NavigationStart),
                tap(() => this.closeMenu()),
                take(1),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe();
    }

    /**
     * Closes the context menu and cleans up all associated resources.
     * Removes event listeners, detaches the overlay, and clears the container.
     */
    closeMenu(): void {
        // Remove event listener before detaching
        if (this.#overlayRef?.overlayElement) {
            this.#overlayRef.overlayElement.removeEventListener('mouseleave', this.#boundMenuMouseLeave);
        }

        if (this.#overlayRef && this.#overlayRef.hasAttached()) {
            this.#overlayRef.detach();
        }

        // Always clean up the overlay container if it exists
        if (this.#contextMenuOverlayContainer) {
            this.#overlayContainerService.removeOverlayContainer(this.#contextMenuOverlayContainer);
            this.#contextMenuOverlayContainer = null;
        }

        this.#menuOpen = false;
    }

    #isInsideSubMenu(element: HTMLElement): boolean {
        // Traverse up the DOM tree to check if the element is inside a ce-context-menu
        while (element !== null) {
            if (element.id === this.#uniqueId) {
                return true;
            }
            element = element.parentElement as HTMLElement;
        }
        return false;
    }

    #createOverlayRef() {
        // Create overlay container based on configuration
        if (this.ceContextMenuContainer()) {
            this.#contextMenuOverlayContainer = this.#overlayContainerService.createOverlayContainerWithinContainer({
                className: 'context-menu-overlay-container',
                container: this.ceContextMenuContainer() as string,
            });
        } else {
            this.#contextMenuOverlayContainer = this.#overlayContainerService.createOverlayContainer(
                'context-menu-overlay-container'
            );
        }

        const positionStrategy = this.#overlay
            .position()
            .flexibleConnectedTo(this.#element)
            .withGrowAfterOpen(true)
            .withFlexibleDimensions(false)
            .withPositions(this.#getPositionStrategy(this.#position));

        this.#overlayRef = this.#overlay.create({
            positionStrategy,
            scrollStrategy: this.#overlay.scrollStrategies.reposition(),
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            panelClass: `ce-overlay-context-menu`,
            disposeOnNavigation: true,
        });

        this.#contextMenuOverlayContainer.appendChild(this.#overlayRef.hostElement);
    }

    /**
     * Gets the position strategy for the context menu based on the configured position.
     * Returns the appropriate connected positions with fallbacks.
     *
     * @param position - The desired menu position
     * @returns Array of connected positions with primary and fallback placements
     */
    #getPositionStrategy(position?: ContextMenuPosition): ConnectedPosition[] {
        if (!position) {
            return POSITION_STRATEGIES['default'];
        }
        return POSITION_STRATEGIES[position] || POSITION_STRATEGIES['bottom-right'];
    }

    ngOnDestroy(): void {
        this.closeMenu();
        if (this.#overlayRef) {
            this.#overlayRef.dispose();
        }
    }
}
